import { createSlice } from '@reduxjs/toolkit';

import initialState from './state';
import { logoutUser } from '../user';
import {
  fetchConsentFormInformation,
  fetchMyCertifications,
  fetchRequiredCertifications,
  fetchWorkerDrugTestingSchedules,
  fetchWorkerThirdPartyDrugTestResults,
  addMyCertification,
  updateMyCertification,
  deleteMyCertification,
  fetchWorkerCourses,
  fetchWorkerProjects,
  fetchEmailContacts,
  fetchPhoneContacts,
  addContact,
  removeContact,
  makePrimaryContact,
  verifyContact,
  fetchProfileCustomFieldsConfig,
} from './actions';
import { FilterCertificateStatus } from 'models/certifications';

const workerDataSlice = createSlice({
  name: 'workerData',
  initialState: initialState,
  reducers: {
    resetWorkerData: state => {
      return initialState;
    },
    resetAdminContacts: state => {
      state.adminContacts = { emails: [], phones: [] };
    },
    resetWorkerProjects: state => {
      state.workerProjects = {
        current: [],
        past: [],
        pending: [],
        pagination: {
          current: { pageNumber: 1, pageSize: 12, totalResults: 0 },
          past: { pageNumber: 1, pageSize: 12, totalResults: 0 },
          pending: { pageNumber: 1, pageSize: 12, totalResults: 0 },
        },
      };
    },
  },
  extraReducers: builder =>
    builder
      .addCase(logoutUser.fulfilled, state => {
        return initialState;
      })
      .addCase(fetchConsentFormInformation.fulfilled, (state, action) => {
        state.consentFormInformation = action.payload;
        return state;
      })
      .addCase(fetchProfileCustomFieldsConfig.fulfilled, (state, action) => {
        state.profileCustomFieldsConfig = action.payload;
        return state;
      })
      .addCase(fetchMyCertifications.fulfilled, (state, action: any) => {
        if (!state.myCertifications) {
          state.myCertifications = { current: null, expired: null, required: null };
        }
        const filterStatus = action.meta.arg.filterCertificateStatus;
        const statusMap = {
          [FilterCertificateStatus.CURRENT]: 'current',
          [FilterCertificateStatus.EXPIRED]: 'expired',
          [FilterCertificateStatus.REQUIRED]: 'required',
        };
        const key = statusMap[filterStatus];
        if (key) state.myCertifications[key] = action.payload;
      })
      .addCase(fetchRequiredCertifications.fulfilled, (state, action: any) => {
        if (!state.myCertifications) {
          state.myCertifications = { current: null, expired: null, required: null };
        }
        state.myCertifications.required = action.payload;
      })
      .addCase(fetchWorkerDrugTestingSchedules.fulfilled, (state, action: any) => {
        state.drugTestingSchedules = action.payload;
      })
      .addCase(fetchWorkerThirdPartyDrugTestResults.fulfilled, (state, action: any) => {
        state.thirdPartyDrugTestResults = action.payload;
      })
      .addCase(fetchWorkerCourses.fulfilled, (state, action) => {
        const payload = action.payload;
        const statusKey = payload.courseStatus === 1 ? 'active' : payload.courseStatus === 3 ? 'expired' : null;

        if (statusKey) {
          if (!state.workerCourses) {
            state.workerCourses = {
              active: [],
              expired: [],
              pagination: {},
            };
          }
          state.workerCourses[statusKey] = payload.data || [];
          if (!state.workerCourses.pagination) {
            state.workerCourses.pagination = {};
          }
          state.workerCourses.pagination[statusKey] = {
            currentPage: payload.currentPage,
            totalPages: payload.totalPages,
            totalRecords: payload.totalRecords,
            limit: payload.limit,
          };
        }
      })
      .addCase(fetchWorkerProjects.fulfilled, (state, action) => {
        const payload = action.payload;
        const statusKey =
          payload.status === 0 ? 'pending' : payload.status === 1 ? 'current' : payload.status === 3 ? 'past' : null;

        if (statusKey) {
          // Single category update (when status is specified)
          if (!state.workerProjects) {
            state.workerProjects = {
              current: [],
              past: [],
              pending: [],
              pagination: {},
            };
          }
          state.workerProjects[statusKey] = payload.data || [];
          if (!state.workerProjects.pagination) {
            state.workerProjects.pagination = {};
          }
          state.workerProjects.pagination[statusKey] = {
            pageNumber: payload.pageNumber,
            pageSize: payload.pageSize,
            totalResults: payload.totalResults,
          };
        } else {
          // All categories update (when no status is specified)
          state.workerProjects = {
            current: payload?.current || [],
            past: payload?.past || [],
            pending: payload?.pending || [],
            pagination: payload?.pagination || state.workerProjects.pagination,
          };
        }
      })
      .addCase(fetchEmailContacts.fulfilled, (state, action: any) => {
        if (action.meta.arg?.isAdminSessionContext) {
          if (!state.adminContacts) state.adminContacts = { emails: [], phones: [] };
          state.adminContacts.emails = action.payload;
        } else {
          state.contacts.emails = action.payload;
        }
      })
      .addCase(fetchPhoneContacts.fulfilled, (state, action: any) => {
        if (action.meta.arg?.isAdminSessionContext) {
          if (!state.adminContacts) state.adminContacts = { emails: [], phones: [] };
          state.adminContacts.phones = action.payload;
        } else {
          state.contacts.phones = action.payload;
        }
      })
      .addMatcher(
        action =>
          action.type === addContact.fulfilled.type ||
          action.type === removeContact.fulfilled.type ||
          action.type === makePrimaryContact.fulfilled.type ||
          action.type === verifyContact.fulfilled.type,
        (state, action: any) => {
          const { contactType, data } = action.payload;
          if (action.meta.arg?.isAdminSessionContext) {
            if (!state.adminContacts) state.adminContacts = { emails: [], phones: [] };
            if (contactType === 0) {
              state.adminContacts.emails = data;
            } else {
              state.adminContacts.phones = data;
            }
          } else {
            if (contactType === 0) {
              state.contacts.emails = data;
            } else {
              state.contacts.phones = data;
            }
          }
        },
      )
      .addMatcher(
        action =>
          action.type === addMyCertification.fulfilled.type ||
          action.type === updateMyCertification.fulfilled.type ||
          action.type === deleteMyCertification.fulfilled.type,
        (state, action: any) => {
          if (!state.myCertifications) {
            state.myCertifications = { current: null, expired: null, required: null };
          }
          const filterStatus = action.payload.filterCertificateStatus;
          const statusMap = {
            [FilterCertificateStatus.CURRENT]: 'current',
            [FilterCertificateStatus.EXPIRED]: 'expired',
            [FilterCertificateStatus.REQUIRED]: 'required',
          };
          const key = statusMap[filterStatus];
          if (key) state.myCertifications[key] = action.payload.data;
        },
      ),
});

export default workerDataSlice;
